import { z } from 'zod'
import { budgetExpenseStatusSchema } from './budget'
import { scheduleColorSchema, scheduleEventCategorySchema } from './schedule'
import {
  supplierCategorySchema,
  supplierRequirementStatusSchema,
  weddingSupplierStatusSchema,
} from './supplier'

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const money = z
  .number()
  .int()
  .min(0, 'Amount cannot be negative')
  .max(100_000_000, 'Amount is too high')

const colorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex color')

const sourceKeySchema = z.string().trim().min(1).max(120)
const excludedSourceKeysSchema = z.array(sourceKeySchema).max(100).default([])

export const VENDOR_SETUP_TASK_SOURCE_SECTIONS = [
  'music-entertainment',
  'photo-video',
  'florist',
  'attire',
  'visual-decor',
  'stationery',
  'beauty',
] as const

export const SETUP_TASK_SOURCE_SECTIONS = [
  'basic',
  'location',
  'guests',
  'ceremony',
  'food',
  ...VENDOR_SETUP_TASK_SOURCE_SECTIONS,
] as const

export type SetupTaskSourceSection = (typeof SETUP_TASK_SOURCE_SECTIONS)[number]
export type VendorSetupTaskSourceSection = (typeof VENDOR_SETUP_TASK_SOURCE_SECTIONS)[number]

export const vendorSetupTaskSourceSectionSchema = z.enum(VENDOR_SETUP_TASK_SOURCE_SECTIONS)

export const SETUP_TASK_SOURCE_SECTION_META = {
  basic: { label: 'Základ', pathSegment: 'basic' },
  location: { label: 'Místo', pathSegment: 'location' },
  guests: { label: 'Hosté', pathSegment: 'guests' },
  ceremony: { label: 'Obřad', pathSegment: 'ceremony' },
  food: { label: 'Jídlo a pití', pathSegment: 'food' },
  'music-entertainment': { label: 'Hudba a zábava', pathSegment: 'music-entertainment' },
  'photo-video': { label: 'Foto a video', pathSegment: 'photo-video' },
  florist: { label: 'Floristky', pathSegment: 'florist' },
  attire: { label: 'Šaty, oblek a příprava', pathSegment: 'attire' },
  'visual-decor': { label: 'Vizuál a dekorace', pathSegment: 'visual-decor' },
  stationery: { label: 'Tiskoviny', pathSegment: 'stationery' },
  beauty: { label: 'Vizážistka a stylistka', pathSegment: 'beauty' },
} satisfies Record<SetupTaskSourceSection, { label: string; pathSegment: string }>

const setupPlanTaskSchema = z.object({
  sourceKey: sourceKeySchema,
  title: z.string().trim().min(1, 'Enter task title').max(160),
  notes: optionalText(2000),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

const budgetCategoryPlanSchema = z.object({
  sourceKey: sourceKeySchema.optional(),
  name: z.string().trim().min(1, 'Enter category name').max(120),
  plannedAmount: money,
  color: colorSchema.default('#c8506e'),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

const budgetExpensePlanSchema = z.object({
  sourceKey: sourceKeySchema,
  categorySourceKey: sourceKeySchema,
  title: z.string().trim().min(1, 'Enter expense title').max(160),
  vendorName: optionalText(160),
  amount: money,
  status: budgetExpenseStatusSchema.default('planned'),
  notes: optionalText(2000),
})

const optionalTime = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm time')
    .optional(),
)

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const setupPlanScheduleEventSchema = z
  .object({
    sourceKey: sourceKeySchema,
    sourceSection: optionalText(80),
    title: z.string().trim().min(1, 'Enter event title').max(180),
    description: optionalText(500),
    category: scheduleEventCategorySchema.default('program'),
    date: optionalDate,
    startMinute: z.number().int().min(0).max(1795),
    endMinute: z.number().int().min(5).max(1800),
    location: optionalText(180),
    notes: optionalText(3000),
    color: scheduleColorSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).default(0),
  })
  .superRefine((value, ctx) => {
    if (value.endMinute <= value.startMinute) {
      ctx.addIssue({
        code: 'custom',
        path: ['endMinute'],
        message: 'End time must be after start time',
      })
      return
    }

    if (value.endMinute - value.startMinute > 1440) {
      ctx.addIssue({
        code: 'custom',
        path: ['endMinute'],
        message: 'Event cannot be longer than 24 hours',
      })
    }
  })

export const applyBasicPlanSchema = z.object({
  tasks: z.array(setupPlanTaskSchema).max(20),
  budgetCategories: z.array(budgetCategoryPlanSchema).max(20),
  excludedSourceKeys: excludedSourceKeysSchema,
})

const setupPlanWeddingSupplierSchema = z.object({
  sourceKey: sourceKeySchema.optional(),
  supplierId: z.uuid(),
  roles: z.array(supplierCategorySchema).min(1, 'Pick at least one role').max(8),
  status: weddingSupplierStatusSchema.default('booked'),
  scope: optionalText(3000),
  agreedAmount: money.optional(),
  depositAmount: money.optional(),
  arrivalTime: optionalTime,
  departureTime: optionalTime,
  notes: optionalText(3000),
})

export const applyLocationPlanSchema = z.object({
  tasks: z.array(setupPlanTaskSchema).max(30),
  weddingSuppliers: z.array(setupPlanWeddingSupplierSchema).max(4),
  budgetCategory: budgetCategoryPlanSchema.optional(),
  budgetExpenses: z.array(budgetExpensePlanSchema).max(10).default([]),
  scheduleEvents: z.array(setupPlanScheduleEventSchema).max(10).default([]),
  excludedSourceKeys: excludedSourceKeysSchema,
})

const guestGroupPlanSchema = z.object({
  sourceKey: sourceKeySchema,
  name: z.string().trim().min(1, 'Enter group name').max(120),
  description: optionalText(1000),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

export const applyGuestsPlanSchema = z.object({
  tasks: z.array(setupPlanTaskSchema).max(30),
  guestGroups: z.array(guestGroupPlanSchema).max(20),
  weddingSuppliers: z.array(setupPlanWeddingSupplierSchema).max(4).default([]),
  budgetCategory: budgetCategoryPlanSchema.optional(),
  budgetExpenses: z.array(budgetExpensePlanSchema).max(10).default([]),
  scheduleEvents: z.array(setupPlanScheduleEventSchema).max(10).default([]),
  excludedSourceKeys: excludedSourceKeysSchema,
})

const setupPlanSupplierRequirementSchema = z.object({
  sourceKey: sourceKeySchema,
  category: supplierCategorySchema,
  status: supplierRequirementStatusSchema.default('searching'),
  title: z.string().trim().min(1, 'Enter requirement title').max(180),
  desiredScope: optionalText(3000),
  targetBudget: money.optional(),
  notes: optionalText(3000),
})

export const applyCeremonyPlanSchema = z.object({
  tasks: z.array(setupPlanTaskSchema).max(20),
  weddingSuppliers: z.array(setupPlanWeddingSupplierSchema).max(2).default([]),
  supplierRequirements: z.array(setupPlanSupplierRequirementSchema).max(4).default([]),
  scheduleEvents: z.array(setupPlanScheduleEventSchema).max(5).default([]),
  excludedSourceKeys: excludedSourceKeysSchema,
})

export const applyFoodPlanSchema = z.object({
  tasks: z.array(setupPlanTaskSchema).max(30),
  weddingSuppliers: z.array(setupPlanWeddingSupplierSchema).max(12).default([]),
  supplierRequirements: z.array(setupPlanSupplierRequirementSchema).max(20).default([]),
  scheduleEvents: z.array(setupPlanScheduleEventSchema).max(5).default([]),
  excludedSourceKeys: excludedSourceKeysSchema,
})

export const applyVendorSectionPlanSchema = z.object({
  sectionRoute: vendorSetupTaskSourceSectionSchema,
  tasks: z.array(setupPlanTaskSchema).max(80),
  weddingSuppliers: z.array(setupPlanWeddingSupplierSchema).max(30).default([]),
  supplierRequirements: z.array(setupPlanSupplierRequirementSchema).max(30).default([]),
  budgetCategory: budgetCategoryPlanSchema.optional(),
  budgetExpenses: z.array(budgetExpensePlanSchema).max(30).default([]),
  excludedSourceKeys: excludedSourceKeysSchema,
})

export type ApplyBasicPlanInput = z.infer<typeof applyBasicPlanSchema>
export type ApplyLocationPlanInput = z.infer<typeof applyLocationPlanSchema>
export type ApplyGuestsPlanInput = z.infer<typeof applyGuestsPlanSchema>
export type ApplyCeremonyPlanInput = z.infer<typeof applyCeremonyPlanSchema>
export type ApplyFoodPlanInput = z.infer<typeof applyFoodPlanSchema>
export type ApplyVendorSectionPlanInput = z.infer<typeof applyVendorSectionPlanSchema>
